import { Stack, SxProps } from '@mui/material';
import { groupBy, map } from 'lodash';
import { useMemo } from 'react';

import { Divider } from '@/components/Divider';
import { COLORS } from '@/styles/tokens/colors';

import { SidebarGroupHeader } from './components/SidebarGroupHeader';
import { MenuItem, MenuItemProps } from './MenuItem';

type MenuItemPropsWithGroup = MenuItemProps & {
  group?: string;
};

interface GroupedSidebarMenuProps {
  items: MenuItemPropsWithGroup[];
  isExpanded: boolean;
  activeTo?: string;
  commonItemProps?: Partial<MenuItemProps>;
  collapsedDividerSx?: SxProps;
}

export function GroupedSidebarMenu({
  items,
  isExpanded,
  activeTo,
  commonItemProps,
  collapsedDividerSx,
}: GroupedSidebarMenuProps) {
  const groups = useMemo(() => groupBy(items, (i) => i.groupName), [items]);

  const mergedCommon = useMemo(
    (): Partial<MenuItemProps> => ({
      colorTheme: 'dark',
      variant: isExpanded ? 'large' : 'small',
      typographyProps: { variant: 'body-xs' },
      ...commonItemProps,
    }),
    [isExpanded, commonItemProps]
  );

  const isLightTheme = mergedCommon.colorTheme === 'light';
  const dividerColor = isLightTheme ? COLORS.NAVY[100] : COLORS.NAVY[400];

  const divider = !isExpanded ? (
    <Divider
      sx={{
        width: '50%',
        mx: '25% !important',
        borderColor: dividerColor,
        ...collapsedDividerSx,
      }}
    />
  ) : undefined;

  return (
    <Stack spacing={isExpanded ? 3 : 1} divider={divider}>
      {map(groups, (groupItems, groupName) => (
        <Stack key={groupName} spacing={0.5}>
          {isExpanded && <SidebarGroupHeader groupName={groupName} />}
          <Stack>
            {groupItems.map((item) => (
              <MenuItem
                key={item.text}
                isActive={activeTo === item.to}
                showTooltip={!isExpanded}
                {...mergedCommon}
                {...item}
              />
            ))}
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
}
